import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BrassRule from "@/components/BrassRule";
import ScrollReveal from "@/components/ScrollReveal";
import ExperienceCard from "@/components/blocks/ExperienceCard";
import CTAStrip from "@/components/blocks/CTAStrip";
import { fetchExperience, fetchExperiences } from "@/lib/api";
import { useLang, CITY_LABELS, COUNTRY_LABELS } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";
import { useAuth } from "@/lib/auth";
import { formatPriceShort, priceDetail } from "@/lib/price";

// SEO wrapper rendered at the top of the detail view
function DetailSeo({ exp }) {
  const { lang } = useLang();
  const countryName = (COUNTRY_LABELS[exp.country] && COUNTRY_LABELS[exp.country][lang]) || exp.country;
  useSeo({
    title: `${exp.title} — Viaja de Verdad`,
    description: exp.subtitle || exp.summary || `${exp.title}, ${countryName}.`,
    image: exp.hero_image,
    locale: lang === "es" ? "es_ES" : "en_US",
    type: "article",
  });
  return null;
}

// ============================================================================
// 1) Detail hero — left-aligned, Ken Burns image, editorial meta-row
// ============================================================================
function DetailHero({ exp, t, lang }) {
  return (
    <section
      data-testid="detail-hero"
      className="relative h-[78vh] min-h-[560px] w-full overflow-hidden bg-charcoal text-ivory"
    >
      <picture>
        {/\.jpe?g$/i.test(exp.hero_image || "") && (
          <source srcSet={exp.hero_image.replace(/\.jpe?g$/i, ".webp")} type="image/webp" />
        )}
        <img
          src={exp.hero_image}
          alt={exp.title}
          className="absolute inset-0 h-full w-full object-cover page-hero-kb"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      </picture>
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,15,15,0.30) 0%, rgba(15,15,15,0.0) 30%, rgba(15,15,15,0.0) 55%, rgba(15,15,15,0.78) 100%)",
        }}
        aria-hidden
      />
      <div className="relative z-10 h-full">
        <div className="mx-auto max-w-7xl h-full px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <div className="max-w-4xl hero-legible">
            <p
              className="eyebrow text-brass mb-5 animate-rise-in"
              style={{ animationDelay: "180ms", opacity: 0 }}
              data-testid="detail-eyebrow"
            >
              {t.detail.experience_eyebrow_pre} · {COUNTRY_LABELS[exp.country][lang].toUpperCase()}
            </p>
            <h1
              className="font-display text-ivory font-normal leading-[1.02] tracking-tight animate-rise-in text-balance"
              style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", animationDelay: "320ms", opacity: 0 }}
              data-testid="detail-headline"
            >
              <SplitTitle title={exp.title} tail={exp.title_italic_tail} />
            </h1>
            <p
              className="eyebrow text-ivory/90 mt-7 animate-rise-in"
              style={{ animationDelay: "500ms", opacity: 0, letterSpacing: "0.22em", fontSize: 11 }}
            >
              {exp.city_label} · {exp.duration} · {(exp.tag_labels || []).join(" · ")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// Render "Main words" + (italic) "tail"
function SplitTitle({ title, tail }) {
  if (!tail || !title) return <>{title}</>;
  const idx = title.toLowerCase().lastIndexOf(String(tail).toLowerCase());
  if (idx < 0) {
    return (
      <>
        {title}
        <br />
        <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{tail}</span>
      </>
    );
  }
  const head = title.slice(0, idx).trimEnd();
  const middle = title.slice(idx, idx + String(tail).length);
  const after = title.slice(idx + String(tail).length);
  return (
    <>
      {head}{" "}
      <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{middle}</span>
      {after}
    </>
  );
}

// ============================================================================
// 2) Intro section
// ============================================================================
function DetailIntro({ exp, t }) {
  const paragraphs = Array.isArray(exp.narrative) ? exp.narrative : [exp.summary];
  return (
    <section className="bg-ivory" data-testid="detail-intro">
      <div className="mx-auto max-w-3xl px-6 md:px-10 py-28 md:py-36 text-center">
        <ScrollReveal>
          <p className="eyebrow text-charcoal/80">{t.detail.intro_eyebrow}</p>
        </ScrollReveal>
        <ScrollReveal delay={120}>
          <h2 className="font-display text-charcoal mt-8 leading-[1.1] text-balance" style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.5rem)" }}>
            {exp.subtitle}
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={220}>
          <div className="mt-12 space-y-7 text-charcoal/85 text-[17px] md:text-[18px] leading-[1.9] text-pretty text-left">
            {paragraphs.map((p, i) => (<p key={`p-${i}-${p.slice(0, 24)}`}>{p}</p>))}
          </div>
        </ScrollReveal>
        <ScrollReveal delay={320}>
          <BrassRule className="mt-14" width={60} />
        </ScrollReveal>
      </div>
    </section>
  );
}

// ============================================================================
// 3) Meta block — ficha técnica
// ============================================================================
function DetailMeta({ exp, t, lang }) {
  const items = [
    { label: t.detail.duration, value: exp.duration },
    { label: t.detail.perfect_for, value: exp.perfect_for, italic: true },
    { label: t.detail.curated_by, value: exp.partner },
    { label: t.detail.best_season, value: exp.best_season },
  ];
  const priceShort = formatPriceShort(exp, lang);
  if (priceShort) {
    items.push({
      label: t.detail.price || "Price",
      value: priceShort,
    });
  }
  const cols = items.length;
  const gridClass =
    cols === 5
      ? "grid grid-cols-2 md:grid-cols-5 gap-x-8 gap-y-12 md:divide-x md:divide-mist"
      : "grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-12 md:divide-x md:divide-mist";
  return (
    <section
      data-testid="detail-meta"
      className="py-20 md:py-24"
      style={{ backgroundColor: "var(--color-pearl)" }}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className={gridClass}>
          {items.map((it, idx) => (
            <ScrollReveal key={it.label} delay={idx * 90} className="md:px-6">
              <p className="eyebrow text-brass mb-3">{it.label}</p>
              {it.italic ? (
                <p className="italic-quote text-charcoal text-lg leading-snug">{it.value}</p>
              ) : (
                <p
                  className="font-display text-charcoal text-xl md:text-2xl leading-tight"
                  data-testid={it.label.toLowerCase() === "price" || it.label.toLowerCase() === "precio" ? `detail-price` : undefined}
                >{it.value}</p>
              )}
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// 3.5) Per-group pricing table (only rendered for per_group with tiers)
// ============================================================================
function DetailPriceTable({ exp, t, lang }) {
  const pd = priceDetail(exp, lang);
  if (!pd || pd.kind !== "per_group") return null;
  return (
    <section
      className="py-16 md:py-20"
      style={{ backgroundColor: "var(--color-ivory)" }}
      data-testid="detail-price-table"
    >
      <div className="mx-auto max-w-3xl px-6 md:px-10">
        <ScrollReveal>
          <p className="eyebrow text-brass mb-4">{t.detail.price || "Price"}</p>
          <h3 className="font-display text-charcoal mb-8 leading-tight" style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)" }}>
            {lang === "es" ? "Tarifa por grupo" : "Group rate"}
          </h3>
        </ScrollReveal>
        <div className="border-t border-mist">
          {pd.tiers.map((row, i) => (
            <ScrollReveal key={row.max_pax} delay={i * 60}>
              <div className="flex justify-between items-baseline border-b border-mist py-4">
                <span className="italic-quote text-charcoal/85 text-lg">
                  {lang === "es" ? `Grupo de ${row.max_pax}` : `Group of ${row.max_pax}`}
                </span>
                <span className="font-display text-charcoal text-xl md:text-2xl">
                  {row.rawSign}{Number.isInteger(row.price) ? row.price : row.price.toFixed(0)}
                </span>
              </div>
            </ScrollReveal>
          ))}
        </div>
        {pd.notes && (
          <p className="italic-quote text-charcoal/70 mt-6 text-[15px] leading-relaxed">{pd.notes}</p>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// 4) Highlights section
// ============================================================================
function DetailHighlights({ exp, t }) {
  const items = exp.highlights || [];
  return (
    <section className="bg-ivory py-28 md:py-36" data-testid="detail-highlights">
      <div className="mx-auto max-w-5xl px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <ScrollReveal><p className="eyebrow text-charcoal/80">{t.detail.highlights_eyebrow}</p></ScrollReveal>
          <ScrollReveal delay={120}>
            <h2 className="font-display text-charcoal mt-6 leading-[1.05] text-balance" style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.5rem)" }}>
              {t.detail.highlights_title_pre}{" "}
              <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{t.detail.highlights_title_italic}</span>
            </h2>
          </ScrollReveal>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-12">
          {items.map((h, i) => (
            <ScrollReveal key={`hl-${i}-${(typeof h === "string" ? h : h?.title || "").slice(0, 24)}`} delay={(i % 2) * 120}>
              <div className="flex gap-5">
                <span className="font-display text-brass leading-none" style={{ fontSize: "clamp(2rem, 3vw, 2.6rem)" }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="text-charcoal/90 text-[17px] leading-[1.7]">{h}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// 5) Gallery — cinematic magazine layout w/ subtle parallax on lead image
// ============================================================================
function ParallaxImg({ src, alt, ratio = "16 / 9" }) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = wrap.getBoundingClientRect();
        const vh = window.innerHeight || 800;
        // -0.5 (top of viewport) to 0.5 (bottom)
        const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
        const translate = Math.max(-1, Math.min(1, progress)) * -7; // ±7%
        img.style.transform = `translate3d(0, ${translate}%, 0) scale(1.08)`;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return (
    <div ref={wrapRef} className="relative w-full overflow-hidden bg-mist" style={{ aspectRatio: ratio }}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ transform: "translate3d(0, 0, 0) scale(1.08)", transition: "transform 80ms linear" }}
      />
    </div>
  );
}

/**
 * EditorialImg — resolution-aware "fotograbado" image.
 *
 *   < 900px wide  → small  : centered card max-w-[520px] on an ivory mat with
 *                            an italic Cormorant caption. We never upscale
 *                            beyond 1.3x the natural width.
 *   900–1499px    → mid    : centered card max-w-[920px] on a soft mist mat.
 *   >= 1500px     → full   : full-bleed at the requested aspect ratio.
 *
 * Until the image loads we keep the requested ratio as a placeholder so the
 * layout doesn't jump.
 */
function EditorialImg({ src, alt, ratio = "4 / 5", caption, forceFull = false }) {
  const [tier, setTier] = useState(null); // "small" | "mid" | "full"
  const [naturalW, setNaturalW] = useState(0);

  function classify(w) {
    if (forceFull) return "full";
    if (w >= 1500) return "full";
    if (w >= 900) return "mid";
    return "small";
  }

  function onLoad(e) {
    const w = e.currentTarget.naturalWidth || 0;
    setNaturalW(w);
    setTier(classify(w));
  }

  // Until decoded: render at the requested ratio so layout doesn't shift.
  if (tier === null) {
    return (
      <div className="relative w-full overflow-hidden bg-mist" style={{ aspectRatio: ratio }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={onLoad}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 w-full h-full object-cover opacity-0"
        />
      </div>
    );
  }

  if (tier === "full") {
    return (
      <div className="relative w-full overflow-hidden bg-mist" style={{ aspectRatio: ratio }}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 w-full h-full object-cover hover:scale-[1.03] transition-transform duration-1500 ease-out"
        />
      </div>
    );
  }

  // Editorial "fotograbado" — small/mid : centered card, no upscale > 1.3x
  const matWidth = tier === "mid" ? 920 : 520;
  const cap = caption || alt;
  return (
    <div
      className="relative mx-auto"
      style={{ maxWidth: `${matWidth}px` }}
      data-testid={`editorial-img-${tier}`}
    >
      <div
        className="relative overflow-hidden"
        style={{
          backgroundColor: "var(--color-ivory, #f6f1e7)",
          padding: tier === "mid" ? "28px" : "22px",
          border: "1px solid rgba(184, 154, 95, 0.18)",
          boxShadow:
            "0 1px 0 rgba(0,0,0,0.04), 0 30px 60px -30px rgba(31,32,34,0.18), 0 12px 24px -20px rgba(31,32,34,0.12)",
        }}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="block w-full h-auto"
          style={{
            // never scale beyond 1.3x natural width
            maxWidth: naturalW ? `${Math.round(naturalW * 1.3)}px` : "100%",
            margin: "0 auto",
          }}
        />
      </div>
      {cap && (
        <p
          className="italic-quote text-charcoal/55 text-center mt-4"
          style={{ fontSize: "0.95rem", lineHeight: 1.4 }}
        >
          {cap}
        </p>
      )}
    </div>
  );
}

// kept for legacy callsites (none anymore) — alias of EditorialImg
function LazyImg(props) {
  return <EditorialImg {...props} />;
}

function DetailGallery({ exp, t }) {
  const all = [exp.hero_image, ...(exp.gallery || [])].filter(Boolean);
  if (all.length < 2) return null;
  // Compose: [0]=lead full-bleed parallax, [1]+[2]=two cols, [3]=full 21:9, optional [4]+[5]=two cols
  const [lead, a, b, c, d, e] = all;
  return (
    <section className="py-28 md:py-36" style={{ backgroundColor: "var(--color-pearl)" }} data-testid="detail-gallery">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <ScrollReveal><p className="eyebrow text-charcoal/80">{t.detail.gallery_eyebrow}</p></ScrollReveal>
          <ScrollReveal delay={120}>
            <h2 className="font-display text-charcoal mt-6 leading-[1.05] text-balance" style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.5rem)" }}>
              {t.detail.gallery_title_pre}{" "}
              <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{t.detail.gallery_title_italic}</span>
            </h2>
          </ScrollReveal>
        </div>

        <ScrollReveal>
          <ParallaxImg src={lead} alt={exp.title} ratio="16 / 9" />
        </ScrollReveal>

        {(a || b) && (
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            {a && <ScrollReveal><LazyImg src={a} alt={exp.title} ratio="4 / 5" /></ScrollReveal>}
            {b && <ScrollReveal delay={120}><LazyImg src={b} alt={exp.title} ratio="4 / 5" /></ScrollReveal>}
          </div>
        )}

        {c && (
          <ScrollReveal>
            <div className="mt-7"><LazyImg src={c} alt={exp.title} ratio="21 / 9" /></div>
          </ScrollReveal>
        )}

        {(d || e) && (
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-7">
            {d && <ScrollReveal><LazyImg src={d} alt={exp.title} ratio="4 / 5" /></ScrollReveal>}
            {e && <ScrollReveal delay={120}><LazyImg src={e} alt={exp.title} ratio="4 / 5" /></ScrollReveal>}
          </div>
        )}
      </div>
    </section>
  );
}

// ============================================================================
// 6) Pull quote — editorial emerald banner
// ============================================================================
function DetailPullQuote({ exp }) {
  if (!exp.pull_quote) return null;
  return (
    <section
      data-testid="detail-pullquote"
      className="relative overflow-hidden text-ivory"
      style={{ backgroundColor: "var(--color-emerald)" }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-brass/35" aria-hidden />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-brass/35" aria-hidden />
      <div className="mx-auto max-w-5xl px-6 md:px-10 py-28 md:py-32 text-center">
        <ScrollReveal>
          <blockquote
            className="leading-[1.18] text-balance"
            style={{ fontSize: "clamp(1.75rem, 3.6vw, 2.65rem)", fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic", fontWeight: 400 }}
          >
            &ldquo;{exp.pull_quote}&rdquo;
          </blockquote>
        </ScrollReveal>
        <ScrollReveal delay={140}>
          <div className="mt-9 flex items-center justify-center gap-4">
            <span className="brass-rule" style={{ width: 36 }} />
            <span className="eyebrow text-brass">{exp.pull_quote_attribution || exp.partner}</span>
            <span className="brass-rule" style={{ width: 36 }} />
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// ============================================================================
// 7) Curated with — credit roll
// ============================================================================
function DetailCurated({ exp, t }) {
  return (
    <section className="bg-ivory py-20 md:py-28" data-testid="detail-curated">
      <div className="mx-auto max-w-3xl px-6 md:px-10 text-center">
        <ScrollReveal><p className="eyebrow text-charcoal/80">{t.detail.curated_eyebrow}</p></ScrollReveal>
        <ScrollReveal delay={120}>
          <h3 className="font-display text-charcoal mt-6" style={{ fontSize: "clamp(1.65rem, 2.8vw, 2.2rem)" }}>
            {exp.partner}
          </h3>
        </ScrollReveal>
        {exp.partner_note && (
          <ScrollReveal delay={220}>
            <p className="italic-quote text-charcoal/85 mt-5 text-balance" style={{ fontSize: "clamp(1.05rem, 1.3vw, 1.25rem)" }}>
              {exp.partner_note}
            </p>
          </ScrollReveal>
        )}
        <ScrollReveal delay={320}><BrassRule className="mt-10" width={40} /></ScrollReveal>
      </div>
    </section>
  );
}

// ============================================================================
// 8) CTAStrip — "Explore the full collection"
// ============================================================================
function DetailCTAStrip({ exp, t, lang }) {
  const { user } = useAuth();
  const back = exp.gallery && exp.gallery[0] ? exp.gallery[0] : exp.hero_image;
  return (
    <section className="relative overflow-hidden text-ivory" data-testid="detail-cta-strip" style={{ minHeight: "60vh" }}>
      {back && <img src={back} alt="" className="absolute inset-0 w-full h-full object-cover cta-kb" loading="lazy" decoding="async" />}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(90deg, rgba(31,77,60,0.88) 0%, rgba(31,77,60,0.62) 100%)" }}
        aria-hidden
      />
      <div className="relative z-10 mx-auto max-w-4xl px-6 md:px-10 py-28 md:py-36 text-center">
        <ScrollReveal><p className="eyebrow text-brass">{t.detail.cta_eyebrow}</p></ScrollReveal>
        <ScrollReveal delay={120}>
          <h2 className="font-display text-ivory mt-6 leading-[1.05] text-balance" style={{ fontSize: "clamp(2.25rem, 5vw, 3.5rem)" }}>
            {t.detail.cta_main}{" "}
            <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{t.detail.cta_italic}</span>
          </h2>
        </ScrollReveal>
        <ScrollReveal delay={220}>
          <p className="italic-quote text-ivory/85 mt-5 text-balance" style={{ fontSize: "clamp(1.1rem, 1.4vw, 1.35rem)" }}>
            {t.detail.cta_sub}
          </p>
        </ScrollReveal>
        <ScrollReveal delay={320}>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5">
            <Link to="/experiences" className="btn-brass text-ivory inline-flex" data-testid="detail-cta-button">
              {t.detail.cta_button}
            </Link>
            {user?.show_prices && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/experiences/${exp.slug}/pdf`}
                className="btn-brass text-ivory inline-flex"
                data-testid="detail-pdf-button"
                style={{ borderStyle: "dashed" }}
              >
                ⬇  Download PDF
              </a>
            )}
          </div>
        </ScrollReveal>
        <ScrollReveal delay={400}>
          <div className="mt-8">
            <Link to={`/${exp.country}`} className="eyebrow text-ivory/85 link-underline" style={{ fontSize: 11 }}>
              ← {t.detail.back_to_country} {COUNTRY_LABELS[exp.country][lang]}
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

// ============================================================================
// 9) Related experiences
// ============================================================================
function DetailRelated({ exp, all, t }) {
  const related = useMemo(() => {
    if (!all || !all.length) return [];
    // Prefer same city, then same country, then same category
    const others = all.filter((x) => x.slug !== exp.slug);
    const sameCity = others.filter((x) => x.city === exp.city);
    const sameCountry = others.filter((x) => x.country === exp.country && x.city !== exp.city);
    const cats = new Set(exp.categories || []);
    const sameCat = others.filter((x) => (x.categories || []).some((c) => cats.has(c)) && x.country !== exp.country);
    const merged = [];
    for (const list of [sameCity, sameCountry, sameCat]) {
      for (const e of list) {
        if (!merged.find((m) => m.slug === e.slug)) merged.push(e);
        if (merged.length >= 3) return merged;
      }
    }
    return merged.slice(0, 3);
  }, [all, exp]);

  if (related.length === 0) return null;

  return (
    <section className="bg-ivory py-28 md:py-32" data-testid="detail-related">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <ScrollReveal><p className="eyebrow text-charcoal/80">{t.detail.related_eyebrow}</p></ScrollReveal>
          <ScrollReveal delay={120}>
            <h2 className="font-display text-charcoal mt-6 leading-[1.05] text-balance" style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.5rem)" }}>
              {t.detail.related_title_pre}{" "}
              <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{exp.city_label}.</span>
            </h2>
          </ScrollReveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {related.map((e, idx) => (
            <ScrollReveal key={e.slug} delay={(idx % 3) * 100}>
              <ExperienceCard experience={e} />
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Main page
// ============================================================================
export default function ExperienceDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [exp, setExp] = useState(null);
  const [allOfCountry, setAllOfCountry] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setExp(null); setError(null);
    fetchExperience(slug, { lang })
      .then((data) => { if (!cancelled) setExp(data); })
      .catch((e) => { if (!cancelled) setError(e); });
    return () => { cancelled = true; };
  }, [slug, lang]);

  useEffect(() => {
    if (!exp) return;
    let cancelled = false;
    fetchExperiences({ country: exp.country, lang })
      .then((data) => { if (!cancelled) setAllOfCountry(data); })
      .catch(console.error);
    return () => { cancelled = true; };
  }, [exp, lang]);

  if (error) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-ivory pt-32">
        <div className="text-center max-w-md">
          <p className="eyebrow text-brass">404</p>
          <h1 className="font-display mt-4" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>Experience not found.</h1>
          <Link to="/experiences" className="btn-brass mt-8 inline-flex text-charcoal">{t.detail.back}</Link>
        </div>
      </div>
    );
  }

  if (!exp) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-ivory pt-32">
        <p className="italic-quote text-charcoal/60">…</p>
      </div>
    );
  }

  return (
    <div data-testid="experience-detail" className="bg-ivory">
      <DetailSeo exp={exp} />
      <DetailHero exp={exp} t={t} lang={lang} />
      <DetailIntro exp={exp} t={t} />
      <DetailMeta exp={exp} t={t} lang={lang} />
      <DetailPriceTable exp={exp} t={t} lang={lang} />
      <DetailHighlights exp={exp} t={t} />
      <DetailGallery exp={exp} t={t} />
      <DetailPullQuote exp={exp} />
      <DetailCurated exp={exp} t={t} />
      <DetailCTAStrip exp={exp} t={t} lang={lang} />
      <DetailRelated exp={exp} all={allOfCountry} t={t} />
    </div>
  );
}
